import { gql } from '@apollo/client';



// GET_USERS_LIST Query
export const GET_USERS_LIST = gql`
  query GetUsersList(
    $pageSize: Int!
    $pageOffset: Int!
    $searchText: String = "%"
    $order_by: [user_order_by]
  ) {
    user(
      limit: $pageSize
      offset: $pageOffset
      where: {
        _or: [
          { u_email_id: { _ilike: $searchText } }
          { u_full_name: { _ilike: $searchText } }
          { user_referred_to: { ur_joining_code: { _ilike: $searchText } } }
        ]
        u_deleted_at: { _is_null: true }
        u_role: { _eq: "user" }
      }
      order_by: $order_by
    ) {
      u_id
      u_full_name
      u_first_name
      u_last_name
      u_email_id
      u_phone_number
      u_country_code
      u_is_active
      u_is_verified
      u_created_at
      u_last_online
      u_delete_requested
      u_sales_reps_id
      u_affiliate_id
      u_client_type
      u_pricing_mode
      u_fee_percentage
      u_seller_tier
      u_stripe_account_id
      u_stripe_business_name
      u_stripe_dba_name
      tickets_sold_amount
      u_total_earnings
      u_referral_code
      u_no_of_referrals
      u_app_info
      u_notification_enabled
      u_device_tokens
      u_allowed_roles
      u_teams

      affiliate {
        a_id
        a_first_name
        a_last_name
        a_email_id
        user {
          u_id
          u_full_name
          u_first_name
          u_last_name
          u_email_id
        }
      }

      tickets_aggregate {
        aggregate {
          sum {
            t_active_tickets
            t_sold_tickets
          }
        }
      }
      user_referred_to {
        ur_joining_code
        user_referred_by {
          u_full_name
          u_first_name
          u_last_name
        }
      }
      user_referred_by_aggregate {
        aggregate {
          count
        }
      }
    }
    user_aggregate(
      where: {
        _or: [
          { u_email_id: { _ilike: $searchText } }
          { u_full_name: { _ilike: $searchText } }
          { user_referred_to: { ur_joining_code: { _ilike: $searchText } } }
        ]
        u_deleted_at: { _is_null: true }
        u_role: { _eq: "user" }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

/** Active rows in `super_user` (used for Users table toggle state). */
export const GET_ACTIVE_SUPER_USER_USER_IDS = gql`
  query GetActiveSuperUserUserIds {
    super_user(
      where: {
        _and: [
          { su_is_active: { _eq: true } }
          { su_deleted_at: { _is_null: true } }
        ]
      }
    ) {
      u_id
    }
  }
`;

// GET_USER_COUNTS Query
export const GET_USER_COUNTS = gql`
  query GetUserCounts {
    user_aggregate(where: { u_delete_requested: { _eq: false } }) {
      aggregate {
        count
      }
    }
    activeUsers: user_aggregate(
      where: { 
        u_is_active: { _eq: true }
        u_delete_requested: { _eq: false }
      }
    ) {
      aggregate {
        count
      }
    }
    deactivatedUsers: user_aggregate(
      where: { 
        u_is_active: { _eq: false }
        u_delete_requested: { _eq: false }
      }
    ) {
      aggregate {
        count
      }
    }
    deletedUsers: user_aggregate(
      where: { u_delete_requested: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
  }
`;

// GET_TEAMS Query
export const GET_TEAM_DETAILS = gql`
  query GetTeamDetails($lc_id: uuid!) {
    league_categories(where: { lc_id: { _eq: $lc_id } }) {
      lc_id
      lc_name
      lc_alternate_name
      lc_brand_name
      lc_market_name
    }
  }
`;

// UPDATE_USER_STATUS Mutation
export const UPDATE_USER_STATUS = gql`
  mutation UpdateUserStatus($userIds: [uuid!]!, $isActive: Boolean, $isDeleted: Boolean) {
    update_user(
      where: { u_id: { _in: $userIds } }
      _set: {
        u_is_active: $isActive
        u_delete_requested: $isDeleted
      }
    ) {
      affected_rows
      returning {
        u_id
        u_is_active
        u_delete_requested
      }
    }
  }
`;

export const REACTIVATE_USER = gql`
  mutation ReactivateUser($userId: uuid!, $input: user_set_input!) {
    update_user(
      where: { u_id: { _eq: $userId } }
      _set: $input
    ) {
      affected_rows
      returning {
        u_id
        u_is_active
        u_delete_requested
        u_email_id
        u_first_name
        u_last_name
        u_phone_number
        u_country_code
        u_pricing_mode
        u_client_type
        u_fee_percentage
        u_seller_tier
        u_sales_reps_id
        u_affiliate_id
      }
    }
  }
`;

// SEND_VERIFICATION Mutation
export const SEND_VERIFICATION = gql`
  mutation SendVerification($verifyUserInput: [verifyUserInput!]!) {
    sendVerificationCodeLink(verifyUserInput: $verifyUserInput) {
      message
    }
  }
`;

// UPDATE_USER_TEAMS Mutation
export const UPDATE_USER_TEAMS = gql`
  mutation UpdateUserTeams($userId: uuid!, $teamName: String!) {
    insert_user_team_oneine(object: {
      user_id: $userId
      team_name: $teamName
    }) {
      user_id
      team_name
      team {
        team_name
        team_id
      }
    }
  }
`;

// REMOVE_USER_TEAM Mutation
export const REMOVE_USER_TEAM = gql`
  mutation RemoveUserTeam($userId: uuid!, $teamName: String!) {
    delete_user_team(
      where: {
        user_id: { _eq: $userId }
        team_name: { _eq: $teamName }
      }
    ) {
      affected_rows
    }
  }
`;

// UPDATE_CLIENT_TYPE Mutation
export const UPDATE_CLIENT_TYPE = gql`
  mutation UpdateClientType($userId: uuid!, $clientType: String) {
    update_user(
      where: { u_id: { _eq: $userId } }
      _set: { u_pricing_mode: $clientType }
    ) {
      affected_rows
      returning {
        u_id
      }
    }
  }
`;

// UPDATE_SELLER_TIER Mutation
export const UPDATE_SELLER_TIER = gql`
  mutation UpdateSellerTier($userId: uuid!, $sellerTier: String) {
    update_user(
      where: { u_id: { _eq: $userId } }
      _set: { u_seller_tier: $sellerTier }
    ) {
      affected_rows
      returning {
        u_id
        u_seller_tier
      }
    }
  }
`;

// UPDATE_SALES_REP Mutation
export const UPDATE_SALES_REP = gql`
  mutation UpdateSalesRep($userId: uuid!, $salesRepId: uuid) {
    update_user(
      where: { u_id: { _eq: $userId } }
      _set: { u_sales_reps_id: $salesRepId }
    ) {
      affected_rows
      returning {
        u_id
        u_sales_reps_id
      }
    }
  }
`;



// UPDATE_AFFILIATE Mutation
// export const UPDATE_AFFILIATE = gql`
//   mutation UpdateAffiliate($userId: uuid!, $affiliateId: uuid) {
//     update_user(
//       where: { u_id: { _eq: $userId } }
//       _set: { u_affiliate_id: $affiliateId }
//     ) {
//       affected_rows
//       returning {
//         u_id
//         affiliate {
//           u_id
//           u_full_name
//         }
//       }
//     }
//   }
// `;



export const UPDATE_AFFILIATE = gql`
  mutation update_user($input: user_set_input, $userId: [uuid!]) {
    update_user(
      where: { u_id: { _in: $userId } }
      _set: $input
    ) {
      affected_rows
    }
  }
`;

// ADMIN_CREATE_USER Mutation
export const ADMIN_CREATE_USER = gql`
  mutation AdminCreateUser($input: adminCreateUserInput!) {
    adminCreateUser(input: $input) {
      message
      user {
        u_id
      }
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser(
    $u_first_name: String!
    $u_last_name: String!
    $u_email_id: String!
    $u_phone_number: String!
    $u_country_code: String!
    $u_stripe_business_name: String
  ) {
    insert_user_one(
      object: {
        u_first_name: $u_first_name
        u_last_name: $u_last_name
        u_email_id: $u_email_id
        u_phone_number: $u_phone_number
        u_country_code: $u_country_code
        u_stripe_business_name: $u_stripe_business_name
        u_role: "user"
        u_allowed_roles: [ "super_user"]   # ✅ Fixed - JSON array syntax
        u_is_active: true
        u_is_verified: false
        u_delete_requested: false
      }
    ) {
      u_id
    }
  }
`;
// GET_SALES_REPS Query
export const GET_SALES_REPS = gql`
  query GetSalesReps {
    salesReps: sales_reps(
      where: {
        sr_deleted_at: { _is_null: true }
      }
      order_by: { sr_created_at: desc }
    ) {
      sr_id
      sr_first_name
      sr_last_name
      sr_email
    }
  }
`;

// GET_AFFILIATES Query
export const GET_AFFILIATES = gql`
  query GetAffiliates {
    affiliates: user(
      where: { 
        u_is_active: { _eq: true }
        u_delete_requested: { _eq: false }
        
      }
    ) {
      u_id
      u_first_name
      u_last_name
      u_email_id
    }
  }
`;

// GET_ALL_TEAMS Query (kept for import compatibility)
export const GET_ALL_TEAMS = gql`
  query GetAllTeams {
    teams: team(
      where: { deleted_at: { _is_null: true } }
      order_by: { team_name: asc }
    ) {
      team_id
      team_name
    }
  }
`;

// GET_ALL_LEAGUE_CATEGORIES - canonical list of teams for assignment
export const GET_ALL_LEAGUE_CATEGORIES = gql`
  query GetAllLeagueCategories {
    league_categories(
      where: {
        lc_deleted_at: { _is_null: true }
        lc_abbrev: { _is_null: false }
        lc_is_active: { _eq: true }
      }
      order_by: { lc_name: asc }
    ) {
      lc_id
      lc_abbrev
      lc_name
      lc_colour_code
      l_id
    }
  }
`;


export const GET_USER_TEAMS_WITH_SOLD_STATUS = gql`
  query GetUserTeamsWithSoldStatus($userId: uuid!) {
    user_preference(where: { u_id: { _eq: $userId } }) {
      lc_id
      league_category {
        lc_id
        lc_abbrev
        lc_name
        lc_colour_code
      }
    }
  }
`;

// export const GET_USER_TEAMS_WITH_SOLD_STATUS = gql`
//   query GetUserTeamsWithSoldStatus($userId: uuid!) {
//     user_preference(where: { u_id: { _eq: $userId } }) {
//       lc_id
//       league_category {
//         lc_id
//         lc_abbrev
//         lc_name
//         lc_colour_code
//         l_id
//       }
//     }
//     ticket_placement(
//       where: {
//         tp_status: { _eq: Sold }
//         ticket: { u_id: { _eq: $userId } }
//       }
//     ) {
//       tp_id
//       tp_status
//       ticket {
//         t_id
//         u_id
//         event {
//           e_id
//           e_name
//         }
//       }
//     }
//   }
// `;

export const GET_AVAILABLE_TEAMS_FOR_USER = gql`
  query GetAvailableTeamsForUser($limit: Int = 3000) {
    league_categories(
      where: {
        lc_deleted_at: { _is_null: true }
        lc_abbrev: { _is_null: false }
        lc_is_active: { _eq: true }
      }
      limit: $limit
      order_by: { lc_name: asc }
    ) {
      lc_id
      lc_abbrev
      lc_name
      lc_colour_code
      lc_brand_name
      l_id
    }
  }
`;

export const ADD_USER_PREFERENCE_TEAM = gql`
  mutation AddUserPreferenceTeam($userId: uuid!, $teamId: uuid!) {
    insert_user_preference_one(object: { u_id: $userId, lc_id: $teamId }) {
      id
      u_id
      lc_id
    }
  }
`;

export const REMOVE_USER_PREFERENCE_TEAM = gql`
  mutation RemoveUserPreferenceTeam($userId: uuid!, $teamId: uuid!) {
    delete_user_preference(
      where: { _and: [{ u_id: { _eq: $userId } }, { lc_id: { _eq: $teamId } }] }
    ) {
      affected_rows
    }
  }
`;

export const CLEAR_USER_PREFERENCE_TEAMS = gql`
  mutation ClearUserPreferenceTeams($userId: uuid!) {
    delete_user_preference(where: { u_id: { _eq: $userId } }) {
      affected_rows
    }
  }
`;

export const GET_ALL_LEAGUES = gql`
  query GetAllLeagues {
    leagues(where: { l_is_active: { _eq: true }, l_deleted_at: { _is_null: true } }, order_by: { l_name: asc }) {
      l_id
      l_name
    }
  }
`;

export const GET_USERS_PREFERENCE_TEAMS = gql`
  query GetUsersPreferenceTeams($userIds: [uuid!]!) {
    user_preference(where: { u_id: { _in: $userIds } }) {
      u_id
      lc_id
      league_category {
        lc_id
        lc_name
        lc_abbrev
      }
    }
  }
`;

// SET_USER_TEAMS Mutation - sets u_teams jsonb array directly on user
export const SET_USER_TEAMS = gql`
  mutation SetUserTeams($userId: uuid!, $teams: jsonb!) {
    update_user(
      where: { u_id: { _eq: $userId } }
      _set: { u_teams: $teams }
    ) {
      returning {
        u_id
        u_teams
      }
    }
  }
`;


// SOFT_DELETE_USER Mutation
export const SOFT_DELETE_USER = gql`
  mutation SoftDeleteUser($userId: uuid!) {
    update_user(
      where: { u_id: { _eq: $userId } }
      _set: { 
        u_delete_requested: true,
        u_is_active: false 
      }
    ) {
      affected_rows
      returning {
        u_id
        u_full_name
        u_delete_requested
        u_is_active
      }
    }
  }
`;

// Legacy mutation used by shared table helpers
export const UPDATE_USER = gql`
  mutation UpdateUser($userStatusInput: userStatusInput!) {
    updateUserActivityStatus(userStatusInput: $userStatusInput) {
      message
    }
  }
`;

// Legacy mutation used by shared table helpers
export const ADD_REFERRED_BY = gql`
  mutation addReferredBy ($userId: uuid!, $userReferralId: uuid!) {
    add_referred_by(args: {userid:$userId,userreferralid:$userReferralId}) {
      u_id
      u_full_name
    }
  }
`;
export const GET_ALL_AFFILIATES = gql`
query getAllAffiliates ($pageSize: Int, $pageNumber: Int, $order_by: [affiliates_order_by!] = {}, $search_event: String = "%") {
  affiliates(
    limit: $pageSize
    offset: $pageNumber
    order_by: $order_by
    where: {
      _and: [
        { a_deleted_at: { _is_null: true } }
        {
          _or: [
            { a_email_id: { _ilike: $search_event } }
            { a_first_name: { _ilike: $search_event } }
            { a_last_name: { _ilike: $search_event } }
            {
              user: {
                _or: [
                  { u_full_name: { _ilike: $search_event } }
                  { u_email_id: { _ilike: $search_event } }
                  { u_first_name: { _ilike: $search_event } }
                  { u_last_name: { _ilike: $search_event } }
                ]
              }
            }
          ]
        }
      ]
    }
  ) {
    a_id
    a_first_name
    a_last_name
    a_email_id
    user {
      u_id
      u_referral_code
      u_full_name
      u_total_earnings
      affiliater_ticket_details
      user_referred_by_aggregate(where: {user_referred_to:{u_deleted_at:{_is_null:true}}}) {
        aggregate {
          count
        }
      }
    }
  }
  affiliates_aggregate(
    where: {
      _and: [
        { a_deleted_at: { _is_null: true } }
        {
          _or: [
            { a_email_id: { _ilike: $search_event } }
            { a_first_name: { _ilike: $search_event } }
            { a_last_name: { _ilike: $search_event } }
            {
              user: {
                _or: [
                  { u_full_name: { _ilike: $search_event } }
                  { u_email_id: { _ilike: $search_event } }
                  { u_first_name: { _ilike: $search_event } }
                  { u_last_name: { _ilike: $search_event } }
                ]
              }
            }
          ]
        }
      ]
    }
  ) {
    aggregate {
      count
    }
  }
}
`;